/**
 * Close short (TASK-0251, INC-4): the words, the reason the dialog starts on, and what closing short does.
 * Pure, so the dialog, the PO page and their tests share them. The API decides (`canCloseShort`, and the
 * route refuses with a sentence); these only explain.
 */
import {
  PO_CLOSE_REASONS,
  suggestedCloseReason,
  type PoCloseReason,
} from '@kk-pos/shared/purchasing';
import { countLabel, money, qty } from '@/lib/format';
import type { PoShortLine, PurchaseOrder } from '@/lib/api/types';
import { n } from './api';

export { PO_CLOSE_REASONS };

/** What each reason is called on screen. */
export const CLOSE_REASON_LABEL: Record<PoCloseReason, string> = {
  not_coming: 'Not coming',
  out_of_stock: 'Vendor out of stock',
  backorder_cancelled: 'Backorder cancelled',
  substituted: 'Substituted',
  other: 'Other',
};

/** The reasons in the order the select lists them. */
export const CLOSE_REASON_ORDER: PoCloseReason[] = [
  'not_coming',
  'out_of_stock',
  'backorder_cancelled',
  'substituted',
  'other',
];

/** The reason the dialog starts on, and the sentence that says where it came from (null: a guess). */
export function closeReasonPrefill(shortLines: readonly PoShortLine[]): {
  reason: PoCloseReason;
  why: string | null;
} {
  const suggested = suggestedCloseReason(
    shortLines.map((line) => line.invoiceResolution)
  );
  if (!suggested.fromInvoice) return { reason: suggested.reason, why: null };
  return {
    reason: suggested.reason,
    why:
      suggested.reason === 'substituted'
        ? 'From the invoice: the vendor sent a substitute.'
        : shortLines.length === 1
          ? 'From the invoice: the missing line won’t come.'
          : 'From the invoice: none of the missing lines will come.',
  };
}

/** `2 of 10 not in`, with the unit for pounds, and what the invoice said. */
export function shortLineText(line: PoShortLine): string {
  const unit = line.unit === 'lb' ? ' lb' : '';
  const missing =
    line.receivedQty > 0
      ? `${qty(line.shortQty)}${unit} of ${qty(line.orderedQty)}${unit} not in`
      : `none of ${qty(line.orderedQty)}${unit} came`;
  const said =
    line.invoiceResolution === 'wont_come'
      ? ' (the invoice says it won’t come)'
      : line.invoiceResolution === 'still_coming'
        ? ' (the invoice said it was still coming)'
        : line.invoiceResolution === 'substituted'
          ? ' (substituted on the invoice)'
          : '';
  return `${line.name ?? `Item #${line.productId ?? line.itemId}`}: ${missing}${said}`;
}

/** What closing this order short does, in the Consequences list. */
export function closeShortConsequences(
  po: Pick<PurchaseOrder, 'shortLines' | 'creditToAskCents'>
): string[] {
  const lines = po.shortLines ?? [];
  const out = [
    `${lines.length === 1 ? 'This line' : `These ${countLabel(lines.length, 'line')}`} will no longer count as on order. Stock already received stays.`,
    'Nothing is taken out of stock and no cost changes. The order becomes received.',
    'If the rest turns up later, it can still be received on this order.',
  ];
  const credit = po.creditToAskCents ?? 0;
  if (credit > 0) {
    out.push(
      `${money(credit)} billed for goods that did not come is still to ask from the vendor. Closing short does not change that.`
    );
  }
  return out;
}

/**
 * Why Close short is not offered on an order, in the operator's words (the API says the same when
 * asked anyway), or null when it is.
 */
export function closeShortBlocked(
  po: Pick<PurchaseOrder, 'status' | 'closedShortAt' | 'items' | 'canCloseShort'>
): string | null {
  if (po.canCloseShort) return null;
  if (po.closedShortAt) return 'This order is already closed short.';
  if (po.status === 'draft') return 'This order has not been ordered yet.';
  if (po.status === 'cancelled') return 'This order is cancelled.';
  if (po.status === 'received') return 'Everything on this order has been received.';
  const anyIn = (po.items ?? []).some((item) => n(item.receivedQty) > 0);
  if (!anyIn) return 'Nothing has been received on this order yet: cancel it instead.';
  return 'Every line of this order is in.';
}

/** The close facts in one line: `Closed short Oct 12 by Motti: Vendor out of stock — note`. */
export function closedShortSummary(
  po: Pick<PurchaseOrder, 'closeReason' | 'closeNote' | 'closedShortByName'>,
  when: string
): string {
  const reason = po.closeReason ? CLOSE_REASON_LABEL[po.closeReason] : 'no reason given';
  const who = po.closedShortByName ? ` by ${po.closedShortByName}` : '';
  const note = po.closeNote?.trim() ? ` — ${po.closeNote.trim()}` : '';
  const text = `Closed short ${when}${who}: ${reason}${note}`;
  // Review n4: a note that already ends a sentence gets no second full stop.
  return /[.!?…]$/.test(text) ? text : `${text}.`;
}

/* ── Cancel (TASK-0251 review M1 / m1) ─────────────────────────────────────────────────────── */

/**
 * What cancelling an order nothing came on does, in its confirmation. A draft never counted as on order
 * (only submitted and partly received orders do), so its list leaves that line out (review r2-n1).
 */
export function cancelConsequences(po: Pick<PurchaseOrder, 'status'>): string[] {
  const out = [
    'The order can no longer be received.',
    'Nothing has been received on it, so no stock moves.',
  ];
  if (po.status !== 'draft') out.push('It no longer counts as on order.');
  return out;
}

/**
 * Why an order cannot be cancelled as it is now, and what to do instead, or null when it can. Read from
 * the order itself (the cancel confirmation loads it), never from a list row: a row can be hours old.
 * Something received comes first: such an order is closed short (allowed with an invoice on it), never
 * cancelled, because a cancelled order can be neither received nor invoiced.
 */
export function cancelBlocked(
  po: Pick<PurchaseOrder, 'status' | 'items' | 'invoices' | 'canCloseShort'>
): { why: string; instead: 'close_short' | 'invoices' | null } | null {
  if (po.status === 'cancelled') {
    return { why: 'This order is already cancelled.', instead: null };
  }
  const anyIn = (po.items ?? []).some((item) => n(item.receivedQty) > 0);
  if (anyIn) {
    return po.canCloseShort
      ? {
          why: 'Something has been received on this order, so it is not cancelled. If the rest will not come, close it short: what came stays in stock.',
          instead: 'close_short',
        }
      : {
          why: 'This order has been received, so there is nothing to cancel.',
          instead: null,
        };
  }
  const live = (po.invoices ?? []).filter((invoice) => invoice.voidedAt == null);
  if (live.length > 0) {
    const which =
      live.length === 1
        ? `Invoice ${live[0]!.number ?? '(no number)'} is`
        : `${countLabel(live.length, 'invoice')} are`;
    return {
      why: `${which} recorded on this order, so it cannot be cancelled. Void ${live.length === 1 ? 'it' : 'them'} on the Invoices tab first.`,
      instead: 'invoices',
    };
  }
  if (po.status !== 'draft' && po.status !== 'submitted') {
    return { why: 'This order cannot be cancelled now.', instead: null };
  }
  return null;
}

/* ── Actions on a listed order (the board's late orders, Follow-ups) ───────────────────────── */

export interface OrderActionSet {
  receive: boolean;
  closeShort: boolean;
  cancel: boolean;
}

/**
 * Which actions a listed order gets: Receive while it can take a receipt; Close short once something came;
 * Cancel only when nothing did (a partly received order is closed short, never cancelled).
 */
export function lateOrderActions(order: {
  status: string;
  /** What the row knows was received on the whole order; null when it does not know. */
  receivedQty: number | null;
}): OrderActionSet {
  const open = order.status === 'submitted' || order.status === 'partially_received';
  if (!open) return { receive: false, closeShort: false, cancel: false };
  const anyIn =
    order.status === 'partially_received' ||
    (order.receivedQty != null && order.receivedQty > 0);
  const nothingIn = order.status === 'submitted' && order.receivedQty === 0;
  return { receive: true, closeShort: anyIn, cancel: nothingIn };
}
